import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import McpAccessSection from "@/components/McpAccessSection";
import WhatsAppAccessSection from "@/components/WhatsAppAccessSection";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { apiClient, type APISubscription } from "@/api/client";
import {
  formatSubscriptionHistoryPolicy,
  type PodcastHistoryMode,
} from "@/lib/subscription-history";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface SubscriptionItem {
  slug: string;
  name: string;
  emoji: string;
  notifyEnabled: boolean;
  description: string | null;
  historyMode: PodcastHistoryMode | null;
  importStartDate: string | null;
}

const formatFromSlug = (slug: string) => {
  if (!slug) return "New podcast";
  return slug
    .split(/[-_]+/)
    .map(part => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ""))
    .join(" ")
    .trim() || "New podcast";
};

const normalizeSubscription = (subscription: APISubscription): SubscriptionItem => {
  const slug = subscription.podcast_slug;
  const name =
    subscription.podcast?.display_name?.trim() || subscription.display_name?.trim() || formatFromSlug(slug);
  const emoji = subscription.podcast?.emoji?.trim() || subscription.emoji?.trim() || "🎧";
  const description = subscription.podcast?.description ?? null;

  return {
    slug,
    name,
    emoji,
    notifyEnabled: Boolean(subscription.notify_enabled),
    description,
    historyMode: subscription.history_mode ?? null,
    importStartDate: subscription.import_start_date ?? null,
  };
};

export default function ProfilePage() {
  const { user } = useAuth();
  const isActiveSubscriber = user?.subscription_status === "active";
  const { subscriptions: rawSubscriptions, isLoading, isError, refreshSubscriptions } = useSubscriptions(isActiveSubscriber);
  const [error, setError] = useState<string | null>(null);
  const [updatingSlug, setUpdatingSlug] = useState<string | null>(null);

  // Normalize subscriptions from API format
  const subscriptions = useMemo(
    () => rawSubscriptions.map(normalizeSubscription),
    [rawSubscriptions],
  );

  const userDetails = useMemo(() => {
    if (!user) {
      return { email: "", displayName: "", createdAt: "" };
    }

    const displayName = user.display_name?.trim() || user.email;
    const createdAt = user.created_at ? new Date(user.created_at).toLocaleDateString() : "";

    return { email: user.email, displayName, createdAt };
  }, [user]);

  const userInitial = userDetails.email ? userDetails.email.charAt(0).toUpperCase() : "👤";

  const handleToggleNotification = async (subscription: SubscriptionItem) => {
    const nextState = !subscription.notifyEnabled;
    setUpdatingSlug(subscription.slug);
    setError(null);

    try {
      const result = await apiClient.updateSubscriptionNotification(subscription.slug, nextState);
      if (!result.ok) {
        throw new Error(result.error || "Unexpected response while updating subscription");
      }
      // Refresh subscriptions from SWR cache
      await refreshSubscriptions();
    } catch (err) {
      console.error("Failed to update subscription notification", err);
      setError("We couldn't update that podcast right now. Please try again.");
    } finally {
      setUpdatingSlug(null);
    }
  };

  // Show error from SWR or local error
  const displayError = isError ? "Unable to load your subscriptions right now. Please try again in a moment." : error;

  return (
    <div className={`min-h-screen ${theme.colors.backgroundCream} transition-colors duration-200 flex flex-col`}>
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-10">
          <div className="mb-10">
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Settings</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Manage your account, messaging connection, AI agent access, and podcast notifications.
            </p>
          </div>

          <div className="space-y-8">
            <section className={`bg-white/80 dark:bg-gray-800/90 backdrop-blur ${theme.borders.neubrutalism} rounded-2xl ${theme.shadows.neubrutalismLg} p-6`}>
              <div className="flex items-center gap-4">
                <div className={`flex h-14 w-14 items-center justify-center rounded-full ${theme.colors.warmYellow} text-xl font-semibold text-gray-900 dark:text-white`}>
                  {userInitial}
                </div>
                <div className="min-w-0">
                  <h2 className="break-words text-xl font-semibold text-gray-900 dark:text-white">{userDetails.email}</h2>
                  {userDetails.createdAt && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Member since {userDetails.createdAt}</p>
                  )}
                </div>
              </div>
            </section>

            <WhatsAppAccessSection isActiveSubscriber={isActiveSubscriber} />

            <McpAccessSection isActiveSubscriber={isActiveSubscriber} />

            {isActiveSubscriber ? (
              <section className={`bg-white/80 dark:bg-gray-800/90 backdrop-blur ${theme.borders.neubrutalism} rounded-2xl ${theme.shadows.neubrutalismLg} p-6`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Podcast notifications</h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    Choose which shows send you email updates when new episodes arrive.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <Link
                    to="/podcasts/add"
                    className={`${theme.buttons.primary} rounded-full px-5 py-2 text-sm font-semibold leading-none min-h-[2.75rem] flex items-center justify-center`}
                  >
                    Add podcast
                  </Link>
                  {isLoading && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-primary-orange dark:border-t-primary-orange-dark rounded-full animate-spin" />
                      Loading…
                    </div>
                  )}
                </div>
              </div>

              {displayError && (
                <div className={cn(theme.borders.error, theme.colors.errorBg, theme.text.error, "mb-6 rounded-xl px-4 py-3 text-sm")}>
                  {displayError}
                </div>
              )}

              {isLoading ? (
                <div className="flex justify-center py-12 text-gray-500 dark:text-gray-400">Fetching your podcasts…</div>
              ) : subscriptions.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 px-6 py-12 text-center text-gray-600 dark:text-gray-300">
                  <p className="text-base font-medium">No podcast subscriptions yet.</p>
                  <p className="mt-2 text-sm">
                    Subscribe to your favourite shows from the Episodes page to start receiving notifications.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {subscriptions.map(subscription => {
                    const isUpdating = updatingSlug === subscription.slug;
                    return (
                      <div
                        key={subscription.slug}
                        className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${theme.borders.neubrutalism} bg-white dark:bg-gray-800 rounded-xl px-5 py-4 ${theme.shadows.neubrutalism}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-3xl leading-none">{subscription.emoji}</div>
                          <div>
                            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{subscription.name}</p>
                            <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                              {formatSubscriptionHistoryPolicy(
                                subscription.historyMode,
                                subscription.importStartDate,
                              )}
                            </p>
                            {subscription.description && (
                              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{subscription.description}</p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleNotification(subscription)}
                          disabled={isUpdating}
                          className={`inline-flex items-center justify-center rounded-xl ${theme.borders.neubrutalism} px-4 py-2 text-sm font-semibold ${theme.shadows.neubrutalismSm} transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-primary-orange/40 dark:focus:ring-primary-orange-dark/30 disabled:cursor-not-allowed disabled:opacity-75 ${
                            subscription.notifyEnabled
                              ? `${theme.colors.primaryOrange} text-gray-900 dark:text-white`
                              : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                          }`}
                        >
                          {isUpdating
                            ? "Saving…"
                            : subscription.notifyEnabled
                              ? "Notifications on"
                              : "Notifications off"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              </section>
            ) : (
              <section className={cn(theme.cards.base, "p-6")}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Podcast notifications</h2>
                <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                  Podcast subscriptions and notification settings are available with an active Podwise plan.
                </p>
                <Link
                  to="/paywall"
                  className={cn(theme.buttons.primary, "mt-4 inline-flex rounded-xl px-5 py-3 font-semibold")}
                >
                  Choose a plan
                </Link>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
